/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { WatchFromSoCuAction } from 'client-code/action-details';
import {
    ActionKernelType,
    InfoCardKernelType,
    WatchFromMyShowsActionKernel,
    WatchFromSocuActionKernel,
} from 'client-code/actions';
import { TransitionScreenType } from 'src/core/enums/TransitionScreenType';

import { OVERLAY_TIMEOUT } from '../../config';
import { Channel } from '../../core/entities/Channel';
import { ContentType } from '../../core/enums';
import { IInfoCard, IInfoCardUserInputEvent, InfoCardManager } from '../../core/infocard';
import { NdvrInteractor, VodInteractor } from '../../core/interactors';
import { Locale, LocaleKeys } from '../../core/locale';
import { VuLogger } from '../../core/logging';
import { ScreenControllerTypes, ScreenTaskControllerTypes } from '../../core/navigation';
import { PlaybackContentType } from '../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../core/playback/impls';
import { SharedPrefConstants, SharedPreferences } from '../../core/storage';
import type { AppDispatch, TGetState, TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import { TChannelV2, TVodPlayActionStateType } from '../../core/types';
import { UIOrchestrator, UIOrchestratorState } from '../../core/uiorchestrator';
import { isJumpChannelCcbSwitchEnabled } from '../../core/utils/ccb/CcbSwitchHelper';
import { NdvrPlaybackType } from '../../core/videoplayer/enums';
import { IPlayable } from '../../core/videoplayer/interfaces';
import { VideoPlaybackController } from '../../core/videoplayer/VideoPlaybackController';
import { WhisperTypes } from '../../views/Whisper/WhisperTypes';
import { prepareCreateOnePassOverlay } from '../overlay/createonepassoverlay/createonepassoverlay.reducres';
import { prepareModifyOnePassOverlay } from '../overlay/modifyonepassoverlay/modifyonepassoverlay.reducers';
import { showRecordingOverlay } from '../overlay/recordingoverlay/recordingoverlay.reducers';
import { clearDiskSpaceSharedPreference } from '../overlay/recordoverlay/recordoverlay.reducers';
import { prepareVodResumeStartOverOverlayForWatchAction } from '../overlay/vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';
import {
    collectionScreenTransition,
    programScreenTransition,
} from '../screens/navigation/screensnavigation';
import { setScreenController, setScreenTaskController } from '../screens/screens.reducers';
import { startVideoPlayback } from '../videoplayer/videoplayer.reducers';
import { notifyVodPlayActionState } from '../vodplayaction/vodplayaction.reducers';
import { showWhisper } from '../whisper/whisper.reducer';
import {
    setInfoCardCallerScreenAction,
    setInfoCardGhostModeAction,
    setInfoCardHighlightAction,
    setInfoCardTimerIdAction,
    setLeftNavigationHighlightAction,
    setRightNavigationHighlightAction,
} from './infocard.actions';
import {
    SET_INFOCARD_CALLER_SCREEN,
    SET_INFOCARD_GHOST_MODE,
    SET_INFOCARD_HIGHLIGHT,
    SET_INFOCARD_NAVIGATION_HIGHLIGHT,
    SET_INFOCARD_TIMER_ID,
} from './infocard.types';
import { InfocardNavigationConstants } from './InfocardNavigationConstants';

interface IInfoCardState {
    callerScreenController: string;
    callerScreenTaskController: string;
    highlight: number;
    ghostMode?: boolean;
    timerId: NodeJS.Timeout | null;
    isCursorOverLeftArrow?: boolean;
    isCursorOverRightArrow?: boolean;
}

const initialState: IInfoCardState = {
    callerScreenController: '',
    callerScreenTaskController: ScreenTaskControllerTypes.NO_TASK,
    highlight: 0,
    ghostMode: false,
    timerId: null,
    isCursorOverLeftArrow: false,
    isCursorOverRightArrow: false,
};

const ACTION_BUTTON_PREFIX = 'action_button_items_';
const ACTION_BUTTON_SEPARATOR = '_';

const infocardReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IInfoCardState>>{},
) => {
    switch (type) {
        case SET_INFOCARD_CALLER_SCREEN:
            return {
                ...state,
                callerScreenController: payload.callerScreenController,
                callerScreenTaskController: payload.callerScreenTaskController,
            };
        case SET_INFOCARD_HIGHLIGHT:
            return {
                ...state,
                highlight: payload.highlight,
            };
        case SET_INFOCARD_GHOST_MODE:
            return {
                ...state,
                ghostMode: payload.ghostMode,
            };
        case SET_INFOCARD_TIMER_ID:
            return {
                ...state,
                timerId: payload.timerId,
            };
        case SET_INFOCARD_NAVIGATION_HIGHLIGHT:
            return {
                ...state,
                isCursorOverLeftArrow: payload.isCursorOverLeftArrow,
                isCursorOverRightArrow: payload.isCursorOverRightArrow,
            };
        default:
            return state;
    }
};

// ===============================================================================================
// BELOW LIST OF DISPATCHABLES ARE RELATED TO KEY EVENTS
// ===============================================================================================

const areActionButtonsVisible = (): boolean => {
    const infoCardManager = InfoCardManager.getInstance();
    return infoCardManager.getProperties().actionButtonVisibility;
};

const hideActionButtons = (): void => {
    const infoCardManager = InfoCardManager.getInstance();
    infoCardManager.hideActionButtons();
};

export const dismissInfoCard = () => {
    if (areActionButtonsVisible()) {
        hideActionButtons();
    }
    const infocard = InfoCardManager.getInstance() as IInfoCard;
    const infocardInputHandler = InfoCardManager.getInstance() as IInfoCardUserInputEvent;
    infocard.dismiss();
    infocardInputHandler.onKeyBack();
};

export const onKeyEventResetInfoCardTimer = () => (dispatch: AppDispatch, getState: TGetState) => {
    const { timerId } = getState().infocard;
    if (timerId !== null) {
        clearTimeout(timerId);
        dispatch(setInfoCardTimerIdAction(null));
    }
    const oInfoCardManagerRef = InfoCardManager.getInstance();
    if (oInfoCardManagerRef.isInfoCardVisible()) {
        const timer = setTimeout(() => {
            dismissInfoCard();
        }, OVERLAY_TIMEOUT);
        dispatch(setInfoCardTimerIdAction(timer));
    }
};

export const onKeyBackInfoCard = () => (_dispatch: AppDispatch) => {
    if (areActionButtonsVisible()) {
        hideActionButtons();
        return;
    }
    dismissInfoCard();
};

export const onKeyDownInfoCard = () => (dispatch: AppDispatch, getState: TGetState) => {
    const oInfoCardManagerRef = InfoCardManager.getInstance();
    const { actionButtonVisibility, actionButtonCount } = oInfoCardManagerRef.getProperties();

    if (!actionButtonVisibility) {
        oInfoCardManagerRef.scroll('down');
        return;
    }

    const { ghostMode, highlight } = getState().infocard;
    if (ghostMode) {
        dispatch(setInfoCardGhostModeAction(false));
    }
    const next = actionButtonCount === 0 ? 0 : Math.min(highlight + 1, actionButtonCount);
    dispatch(setInfoCardHighlightAction(next));
};

export const onKeyLeftInfoCard = () => (_dispatch: AppDispatch) => {
    const infocardInputHandler = InfoCardManager.getInstance() as IInfoCardUserInputEvent;
    infocardInputHandler.onKeyLeft();
};

export const onKeyOkInfoCard = () => async (dispatch: AppDispatch, getState: TGetState) => {
    const oInfoCardManagerRef = InfoCardManager.getInstance();
    const { actionButtonVisibility } = oInfoCardManagerRef.getProperties();

    if (!actionButtonVisibility) {
        oInfoCardManagerRef.setProperties({ actionButtonVisibility: true });
        oInfoCardManagerRef.showActionButtons();
        dispatch(setInfoCardHighlightAction(0));
    } else {
        const infoCardSharedPref = new SharedPreferences(
            SharedPrefConstants.INFO_CARD_PREF_KEY,
        ).getJson(SharedPrefConstants.INFO_CARD_OPTIONS) as any;

        if (!infoCardSharedPref) return;

        const highlightIndex = getState().infocard.highlight;
        const selectedOption = infoCardSharedPref[highlightIndex];

        if (highlightIndex === oInfoCardManagerRef.getProperties().actionButtonCount) {
            const kernel: any = oInfoCardManagerRef.getInfoCardKernel();
            dismissInfoCard();
            const vodParams = {
                partnerId: kernel?.partnerId,
                collapsedVodItemId: kernel?.collapsedVodItemId,
                contentSupplierPartnerId: kernel?.contentSupplierPartnerId,
            };

            switch (kernel?.infoCardKernelType) {
                case InfoCardKernelType.ContentInfoCardKernel:
                    dispatch(
                        programScreenTransition(
                            {
                                id: kernel.contentId,
                                contentType: ContentType.CONTENT_ID,
                                ...vodParams,
                            },
                            showWhisper,
                        ),
                    );
                    break;

                case InfoCardKernelType.OfferInfoCardKernel:
                case InfoCardKernelType.VodOfferInfoCardKernel:
                    dispatch(
                        programScreenTransition(
                            {
                                id: kernel.offerId,
                                contentType:
                                    kernel?.infoCardKernelType ===
                                    InfoCardKernelType.VodOfferInfoCardKernel
                                        ? ContentType.VOD_OFFER_ID
                                        : ContentType.OFFER_ID,
                                ...vodParams,
                            },
                            showWhisper,
                        ),
                    );
                    break;

                case InfoCardKernelType.RecordingInfoCardKernel:
                    dispatch(
                        programScreenTransition(
                            {
                                id: kernel.recordingId,
                                contentType: ContentType.RECORDING_ID,
                                ...vodParams,
                            },
                            showWhisper,
                        ),
                    );
                    break;

                case InfoCardKernelType.UnifiedCollectionInfoCardKernel:
                    dispatch(
                        collectionScreenTransition(
                            {
                                unifiedCollectionId: kernel.unifiedCollectionId,
                                transitionScreen: '' as TransitionScreenType,
                                ...vodParams,
                            },
                            showWhisper,
                        ),
                    );
                    break;

                default:
            }
            return;
        }

        const infocard = InfoCardManager.getInstance() as IInfoCard;
        infocard.dismiss();

        switch (selectedOption?.value?.actionKernelType) {
            case ActionKernelType.BookmarkThisEpisodeActionKernel:
            case ActionKernelType.BookmarkThisEventActionKernel:
            case ActionKernelType.BookmarkThisGameActionKernel:
            case ActionKernelType.BookmarkThisMatchActionKernel:
            case ActionKernelType.BookmarkThisMovieActionKernel:
            case ActionKernelType.BookmarkThisRaceActionKernel:
            case ActionKernelType.BookmarkThisShowActionKernel: {
                try {
                    const services = new NdvrInteractor();
                    await services
                        .getRequestModelToCreateBookmarkContentIdSchedule(
                            selectedOption.value.contentId,
                            selectedOption.value.collectionId,
                        )
                        .send();

                    const whisperMessage = Locale.getString(
                        LocaleKeys[
                            `BOOKMARK_WHISPER_MESSAGE_FOR_${selectedOption.value.bookmarkType}_TYPE`
                        ],
                    );
                    UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.BOOKMARK_CREATED);
                    dispatch(
                        showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }),
                    );
                } catch (error) {
                    const whisperMessage = Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE);
                    dispatch(
                        showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }),
                    );
                }
                break;
            }
            case ActionKernelType.RecordThisEpisodeActionKernel:
            case ActionKernelType.RecordThisEventActionKernel:
            case ActionKernelType.RecordThisGameActionKernel:
            case ActionKernelType.RecordThisMatchActionKernel:
            case ActionKernelType.RecordThisRaceActionKernel:
            case ActionKernelType.RecordThisMovieActionKernel:
            case ActionKernelType.RecordThisShowActionKernel: {
                const { contentId, offerId } = selectedOption.value;

                // Extract the record type from ActionKernelType (e.g., "RecordThisEpisodeActionKernel" -> "EPISODE")
                const actionKernelTypeStr = selectedOption.value.actionKernelType as string;
                const recordTypeMatch = actionKernelTypeStr.match(/recordThis(\w+)ActionKernel/);
                const recordType = recordTypeMatch![1].toUpperCase();

                dispatch(
                    showRecordingOverlay(
                        recordType,
                        { contentId, offerId },
                        selectedOption.value.actionKernelType,
                    ),
                );
                break;
            }
            case ActionKernelType.CreateMultiOnePassActionKernel:
            case ActionKernelType.CreateOnePassActionKernelV2: {
                dispatch(handleOnePassAction(selectedOption, 'create'));
                break;
            }
            case ActionKernelType.ModifyMultiOnePassActionKernel:
            case ActionKernelType.ModifyOnePassActionKernelV2: {
                dispatch(handleOnePassAction(selectedOption, 'modify'));
                break;
            }
            case ActionKernelType.WatchLiveActionKernel: {
                const services = new VodInteractor();
                const getChannelFromChannelNumber = isJumpChannelCcbSwitchEnabled()
                    ? services.getChannelFromChannelNumberV2
                    : services.getChannelFromChannelNumber;
                const channelObj = await getChannelFromChannelNumber(
                    selectedOption.value.channelNumber,
                );
                dispatch(startLiveTvFromInfoCard(channelObj));
                break;
            }
            case ActionKernelType.WatchFromSocuActionKernel: {
                dispatch(launchSoCuPlaybackFromInfoCard(selectedOption));
                break;
            }
            case ActionKernelType.WatchFromMyShowsActionKernel: {
                const { recordingId, isRecordingInProgress } =
                    selectedOption.value as WatchFromMyShowsActionKernel;
                dispatch(startRecordingPlayback(recordingId, isRecordingInProgress!));
                break;
            }
            case ActionKernelType.WatchFromVodActionKernel: {
                dispatch(
                    notifyVodPlayActionState({
                        type: TVodPlayActionStateType.INIT,
                        vodRequiredParams: {
                            itemType: 'content',
                            id: selectedOption.value.contentId,
                            partnerId: selectedOption.value.partnerId,
                            collapsedVodItemId: selectedOption.value.collapsedVodItemId,
                            contentSupplierPartnerId: selectedOption.value.contentSupplierPartnerId,
                        },
                    }),
                );
                break;
            }
            case ActionKernelType.WatchFromVodOfferActionKernel: {
                dispatch(
                    notifyVodPlayActionState({
                        type: TVodPlayActionStateType.INIT,
                        vodRequiredParams: {
                            itemType: 'offer',
                            id: selectedOption.value.offerId,
                            partnerId: selectedOption.value?.partnerId || '',
                        },
                    }),
                );
                break;
            }

            default:
        }
    }
};

const startRecordingPlayback =
    (recordingId: string, isRecordingInProgress: boolean) => (dispatch: Function) => {
        clearDiskSpaceSharedPreference();
        const playbackModelImpl = PlaybackDataModelImpl.getInstance();
        playbackModelImpl.setPlaybackData(
            isRecordingInProgress
                ? PlaybackContentType.NDVR_LIVE
                : PlaybackContentType.NDVR_RECORDED,
        );
        const playable: IPlayable = {
            playbackContentType: isRecordingInProgress
                ? PlaybackContentType.NDVR_LIVE
                : PlaybackContentType.NDVR_RECORDED,
            ndvrPlaybackModel: {
                id: recordingId,
                ndvrType: NdvrPlaybackType.RECORDING,
            },
        };
        dispatch(startVideoPlayback(playable));
    };

const launchSoCuPlaybackFromInfoCard = (selectedOption: any) => async (dispatch: Function) => {
    const {
        isCatchUp,
        isStartOver,
        socuOfferId: offerId,
    } = selectedOption.value as WatchFromSocuActionKernel;
    const playbackModelImpl = PlaybackDataModelImpl.getInstance();
    const actionDetailsList = await playbackModelImpl.fetchActionDetails({
        isCatchUp: !!isCatchUp,
        isStartOver: !!isStartOver,
        screenType: 'contentScreen',
        offerId,
    });
    playbackModelImpl.setActionDetailsList(actionDetailsList);
    const watchAction: WatchFromSoCuAction = actionDetailsList.actionDetails.watchActionsList[0];
    dispatch(prepareVodResumeStartOverOverlayForWatchAction(watchAction, isStartOver));
};

const handleOnePassAction =
    (selectedOption: any, type: 'create' | 'modify') => (dispatch: AppDispatch) => {
        const onePassSharedPreferenceEditor = new SharedPreferences(
            SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY,
        ).edit();
        onePassSharedPreferenceEditor.putJson(
            type === 'create'
                ? SharedPrefConstants.CREATE_ONE_PASS_ACTION
                : SharedPrefConstants.MODIFY_ONE_PASS_ACTION,
            selectedOption,
        );
        onePassSharedPreferenceEditor.apply();
        dispatch(type === 'create' ? prepareCreateOnePassOverlay() : prepareModifyOnePassOverlay());
    };

const startLiveTvFromInfoCard = (channel: Channel | TChannelV2) => (dispatch: Function) => {
    const playbackModelImpl = PlaybackDataModelImpl.getInstance();
    const livePlayable: IPlayable = {
        channel,
        playbackContentType: PlaybackContentType.LIVE,
    };
    if (playbackModelImpl.isSocu()) {
        playbackModelImpl.resetPlaybackData();
    } else {
        playbackModelImpl.resetNewPlaybackData();
    }
    if (
        channel.channelNumber !==
        VideoPlaybackController.getInstance().getPlayable()?.channel?.channelNumber
    ) {
        VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.CHANNEL_CHANGE);
    }
    dispatch(startVideoPlayback(livePlayable));
};

export const onKeyRightInfoCard = () => (_dispatch: AppDispatch) => {
    const infocardInputHandler = InfoCardManager.getInstance() as IInfoCardUserInputEvent;
    infocardInputHandler.onKeyRight();
};

export const onKeyUpInfoCard = () => (dispatch: AppDispatch, getState: TGetState) => {
    const oInfoCardManagerRef = InfoCardManager.getInstance();
    const { actionButtonVisibility, actionButtonCount } = oInfoCardManagerRef.getProperties();

    if (!actionButtonVisibility) {
        oInfoCardManagerRef.scroll('up');
        return;
    }

    const { highlight, ghostMode } = getState().infocard;
    if (ghostMode) {
        dispatch(setInfoCardGhostModeAction(false));
    }
    const next = actionButtonCount === 0 ? 0 : Math.max(highlight - 1, 0);
    dispatch(setInfoCardHighlightAction(next));
};

// ===============================================================================================
// BELOW LIST OF DISPATCHABLES ARE RELATED TO MOUSE EVENTS.
// ===============================================================================================

export const onMouseClickInfoCard = () => (dispatch: Function, getState: TGetState) => {
    const { value } = getState().inputFactory;

    switch (value) {
        case InfocardNavigationConstants.INFO_CARD_EAST:
            dispatch(onKeyRightInfoCard());
            break;
        case InfocardNavigationConstants.INFO_CARD_WEST:
            dispatch(onKeyLeftInfoCard());
            break;
        case InfocardNavigationConstants.INFO_CARD_GLOBAL_MOUSE:
            dispatch(setInfoCardGhostModeAction(false));
            dismissInfoCard();
            break;
        case InfocardNavigationConstants.INFO_CARD_SHIELD:
            break;
        default:
            dispatch(onKeyOkInfoCard());
            break;
    }
};

export const onMouseOutInfoCard = () => (dispatch: Function, getState: TGetState) => {
    const oInfoCardManagerRef = InfoCardManager.getInstance();
    const { actionButtonVisibility } = oInfoCardManagerRef.getProperties();
    if (!actionButtonVisibility) return;

    const { screens } = getState();
    const { screenTaskController } = screens;

    if (screenTaskController === ScreenTaskControllerTypes.GLOBAL_INFOCARD_CONTROLLER) {
        dispatch(setInfoCardGhostModeAction(true));
    }
};

export const onMouseOverInfoCard = () => (dispatch: Function, getState: TGetState) => {
    const { inputFactory, infocard } = getState();
    const { value } = inputFactory;
    const { ghostMode } = infocard;

    if (typeof value !== 'string') {
        return;
    }

    if (value === InfocardNavigationConstants.INFO_CARD_WEST) {
        dispatch(setLeftNavigationHighlightAction(true));
        return;
    }

    if (value === InfocardNavigationConstants.INFO_CARD_EAST) {
        dispatch(setRightNavigationHighlightAction(true));
        return;
    }

    let position: number | null = null;
    if (value.startsWith(ACTION_BUTTON_PREFIX)) {
        const parsedPos = Number(value.split(ACTION_BUTTON_SEPARATOR).pop());
        position = Number.isNaN(parsedPos) ? null : parsedPos;
    }

    if (position === null) {
        dispatch(setLeftNavigationHighlightAction(false));
        dispatch(setRightNavigationHighlightAction(false));
        dispatch(setInfoCardGhostModeAction(true));
        return;
    }

    if (ghostMode) {
        dispatch(setInfoCardGhostModeAction(false));
    }
    dispatch(setInfoCardHighlightAction(position));
};

// ===============================================================================================
// BELOW LIST OF DISPATCHABLES ARE RELATED TO CORE INFOCARD OPERATIONS.
// ===============================================================================================

export const onVisibilityShowInfoCardOperations =
    () => (dispatch: Function, getState: TGetState) => {
        const { screenTaskController, screenController } = getState().screens;
        dispatch(setInfoCardCallerScreenAction(screenController, screenTaskController));
        dispatch(setScreenController(ScreenControllerTypes.INFO_CARD_SCREEN));
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.GLOBAL_INFOCARD_CONTROLLER));
        const timerId = setTimeout(() => {
            dismissInfoCard();
        }, OVERLAY_TIMEOUT);
        dispatch(setInfoCardTimerIdAction(timerId));
    };

export const onVisibilityHideInfoCardOperations =
    () => (dispatch: AppDispatch, getState: TGetState) => {
        const { callerScreenController, callerScreenTaskController } = getState().infocard;
        dispatch(setInfoCardCallerScreenAction('', ScreenTaskControllerTypes.NO_TASK));
        dispatch(setScreenTaskController(callerScreenTaskController));
        dispatch(setScreenController(callerScreenController));
    };

export default infocardReducer;
